import {
  HIT_TEST_MARGIN,
  StateNode,
  TLEventHandlers,
  TLKeyboardEvent,
  TLKeyboardEventInfo,
  TLShape,
  TLShapeId,
  Vec2d,
} from '@tldraw/tldraw'

export type Position =
  | 'inside'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'none'

export class Measuring extends StateNode {
  static override id = 'measuring'

  hoveredShapeId: TLShapeId | null = null
  position: Position = 'none'

  override onEnter = () => {
    this.updateHoveredId()
  }

  override onExit = () => {
    this.hoveredShapeId = null
    this.position = 'none'
  }

  override onKeyDown: TLEventHandlers['onKeyDown'] = (info) => {
    switch (info.code) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown': {
        this.nudgeSelectedShapes(false)
        this.calculatePosition()
        break
      }
    }
  }

  override onKeyRepeat: TLEventHandlers['onKeyDown'] = (info) => {
    switch (info.code) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown': {
        this.nudgeSelectedShapes(true)
        this.calculatePosition()
        break
      }
    }
  }

  override onKeyUp: TLKeyboardEvent = (info: TLKeyboardEventInfo) => {
    if (info.key === 'Alt') {
      this.editor.setCurrentTool('select.idle', {})
    }
  }

  private nudgeSelectedShapes(ephemeral = false) {
    const MAJOR_NUDGE_FACTOR = 10
    const MINOR_NUDGE_FACTOR = 1
    const GRID_INCREMENT = 5
    const {
      editor: {
        inputs: { keys },
      },
    } = this

    // We want to use the "actual" shift key state,
    // not the one that's in the editor.inputs.shiftKey,
    // because that one uses a short timeout on release
    const shiftKey = keys.has('ShiftLeft')

    const delta = new Vec2d(0, 0)

    if (keys.has('ArrowLeft')) delta.x -= 1
    if (keys.has('ArrowRight')) delta.x += 1
    if (keys.has('ArrowUp')) delta.y -= 1
    if (keys.has('ArrowDown')) delta.y += 1

    if (delta.equals(new Vec2d(0, 0))) return

    if (!ephemeral) this.editor.mark('nudge shapes')

    const { gridSize } = this.editor.getDocumentSettings()

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    const step = this.editor.getInstanceState().isGridMode
      ? shiftKey
        ? gridSize * GRID_INCREMENT
        : gridSize
      : shiftKey
      ? MAJOR_NUDGE_FACTOR
      : MINOR_NUDGE_FACTOR

    this.editor.nudgeShapes(this.editor.getSelectedShapeIds(), delta.mul(step))
  }

  override onPointerMove: TLEventHandlers['onPointerMove'] = () => {
    this.updateHoveredId()
  }

  private updateHoveredId = () => {
    const hitShape = this.editor.getShapeAtPoint(this.editor.inputs.currentPagePoint, {
      hitInside: false,
      hitLabels: false,
      margin: HIT_TEST_MARGIN / this.editor.getZoomLevel(),
      renderingOnly: true,
    })

    if (!hitShape) {
      this.hoveredShapeId = null
      this.editor.setHoveredShape(null)
      return
    }

    if (hitShape.id === this.hoveredShapeId) {
      return
    }

    let shapeToHover: TLShape | undefined = undefined

    const outermostShape = this.editor.getOutermostSelectableShape(hitShape)

    if (outermostShape === hitShape) {
      shapeToHover = hitShape
    } else {
      if (
        outermostShape.id === this.editor.getFocusedGroupId() ||
        this.editor.getSelectedShapeIds().includes(outermostShape.id)
      ) {
        shapeToHover = hitShape
      } else {
        shapeToHover = outermostShape
      }
    }

    this.hoveredShapeId = shapeToHover.id
    this.editor.setHoveredShape(shapeToHover.id)
    this.calculatePosition()
  }

  private calculatePosition = () => {
    let position: Position = 'none'

    const selectedShape = this.editor.getOnlySelectedShape()
    const hoveredShape = this.editor.getHoveredShape()

    if (!selectedShape || !hoveredShape) {
      this.position = 'none'
      return
    }

    const selectedBounds = this.editor.getShapePageBounds(selectedShape)
    const hoveredBounds = this.editor.getShapePageBounds(hoveredShape)

    if (!selectedBounds || !hoveredBounds) {
      this.position = 'none'
      return
    }

    const selectedRight = selectedBounds.x + selectedBounds.w
    const selectedBottom = selectedBounds.y + selectedBounds.h
    const hoveredRight = hoveredBounds.x + hoveredBounds.w
    const hoveredBottom = hoveredBounds.y + hoveredBounds.h

    if (hoveredBounds.collides(selectedBounds)) {
      position = 'inside'
    }

    if (selectedRight <= hoveredBounds.x) {
      position = 'right'
    } else if (hoveredRight <= selectedBounds.x) {
      position = 'left'
    }

    if (selectedBottom <= hoveredBounds.y) {
      if (position === 'right') {
        position = 'bottom-right'
      } else if (position === 'left') {
        position = 'bottom-left'
      } else {
        position = 'bottom'
      }
    } else if (hoveredBottom <= selectedBounds.y) {
      if (position === 'right') {
        position = 'top-right'
      } else if (position === 'left') {
        position = 'top-left'
      } else {
        position = 'top'
      }
    }

    this.position = position
  }
}
